import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { SURVEY_SERVICE_TYPES } from '../../types';
import { MapPicker } from '../../components/MapPicker';

export const SurveyorOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ latitude: null as number | null, longitude: null as number | null, address: '', service_types: [] as string[] });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'pending'>('form');

  const set = (key: string, val: any) => setForm((f) => ({ ...f, [key]: val }));

  const toggleService = (s: string) => {
    setForm((f) => ({ ...f, service_types: f.service_types.includes(s) ? f.service_types.filter((x) => x !== s) : [...f.service_types, s] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.service_types.length === 0) return setError('Select at least one service type');
    if (form.latitude === null || form.longitude === null) return setError('Location is required to determine your base city');
    setIsLoading(true);
    setError('');
    try {
      await userService.updateSurveyorProfile({
        latitude: form.latitude,
        longitude: form.longitude,
        service_types: form.service_types,
      });
      // Upload each verification document
      for (const file of uploadedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        // Uses the web-compatible File object directly
        try {
          await userService.uploadVerificationDocWeb(formData);
        } catch { /* continue with others if one fails */ }
      }
      setStep('pending');
    } catch (err: any) {
      setError(err?.message || 'Failed to submit profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'pending') {
    return (
      <div style={s.centerPage}>
        <div style={s.pendingCard}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⏳</div>
          <h2 style={s.title}>Verification Pending</h2>
          <p style={s.text}>Your profile has been submitted for verification. An admin will review your documents and approve your account. You'll be notified once approved.</p>
          <button onClick={() => navigate('/surveyor')} style={s.primaryBtn}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h1 style={s.title}>Surveyor Onboarding</h1>
      <p style={{ fontSize: 14, color: 'var(--sc-text-secondary)', marginBottom: 24 }}>Complete your profile to start receiving survey assignments.</p>

      <div style={s.card}>
        {error && <div style={s.errorBox}>{error}</div>}

        <form onSubmit={handleSubmit} style={s.form}>
          <label style={s.label}>Base Location * (Determines your Base City)</label>
          <MapPicker
            latitude={form.latitude}
            longitude={form.longitude}
            onChange={(lat: number, lng: number) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))}
            onSelectAddress={(addr: string) => setForm(f => ({ ...f, address: addr }))}
          />

          <label style={s.label}>Service Types * (select all that apply)</label>
          <div style={s.chipGrid}>
            {SURVEY_SERVICE_TYPES.map((st) => (
              <button key={st} type="button" onClick={() => toggleService(st)}
                style={{ ...s.chip, ...(form.service_types.includes(st) ? s.chipActive : {}) }}>{st}</button>
            ))}
          </div>

          <label style={s.label}>Upload Verification Documents</label>
          <div style={s.uploadBox}>
            <span style={{ fontSize: 28 }}>📁</span>
            <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)', margin: '8px 0' }}>Drag & drop or click to upload qualifications, certifications, ID</p>
            <input type="file" multiple style={{ opacity: 0.7, fontSize: 12, color: 'var(--sc-text-primary)' }}
              onChange={(e) => {
                if (e.target.files) {
                  setUploadedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                }
              }}
            />
            {uploadedFiles.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column' as const, gap: 4, width: '100%' }}>
                {uploadedFiles.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--sc-success-bg)', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                    <span style={{ color: 'var(--sc-success-text)', fontWeight: 600 }}>📄 {f.name}</span>
                    <button type="button" onClick={() => setUploadedFiles(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: 'var(--sc-error)', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={isLoading} style={s.primaryBtn}>
            {isLoading ? 'Submitting...' : 'Submit for Verification'}
          </button>
        </form>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 640, margin: '0 auto', padding: '16px', boxSizing: 'border-box' },
  centerPage: { display: 'flex', justifyContent: 'center', marginTop: 40, padding: '16px' },
  pendingCard: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: '32px 24px', textAlign: 'center' as const, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)', maxWidth: 480, width: '100%', boxSizing: 'border-box' },
  title: { fontSize: 22, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 8px' },
  text: { fontSize: 14, color: 'var(--sc-text-secondary)', lineHeight: '1.6', marginBottom: 24 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: '24px 20px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', boxSizing: 'border-box' },
  errorBox: { background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  form: { display: 'flex', flexDirection: 'column' as const },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--sc-text-primary)', marginTop: 16, marginBottom: 6 },
  input: { height: 48, padding: '0 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, outline: 'none', fontFamily: 'inherit', background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)', boxSizing: 'border-box' },
  chipGrid: { display: 'flex', flexWrap: 'wrap' as const, gap: 8 },
  chip: { padding: '10px 16px', borderRadius: 8, border: '1px solid var(--sc-border)', background: 'var(--sc-bg-primary)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: 'var(--sc-text-secondary)', transition: 'all var(--sc-transition-fast)' },
  chipActive: { borderColor: 'var(--sc-brand-primary)', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', fontWeight: 600 },
  uploadBox: { border: '2px dashed var(--sc-border)', borderRadius: 10, padding: 24, textAlign: 'center' as const, background: 'var(--sc-bg-primary)', boxSizing: 'border-box' },
  primaryBtn: { marginTop: 24, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box' },
};
